import { useEffect, useRef, useState } from "react";
import { Button, EmptyState, Field, Picker, PlatformIcon, Spinner, useDismiss } from "@cntrl/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCapability, useBandContext } from "../../app/bandContext.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { IS_NON_PRODUCTION } from "../../config/env.js";
import { useConnectDevArtist } from "../../app/hooks/useSongstatsDebug.js";
import {
  useConnections,
  useStartConnection,
  useDisconnectConnection,
  useAssignMerchChannel,
  useUpdateSocialHandles,
  useRefreshAllData,
  useConnectYouTube,
  useSpotifyArtistSearch,
  useClaimSpotifyArtist,
  useChartmetricArtistSearch,
  useClaimChartmetricArtist,
  type ConnectionStatus,
  type SpotifyArtistHit,
  type SpotifyArtistReservation,
  type ChartmetricArtistHit,
  type ChartmetricArtistLinkView,
  type MerchChannel,
  type SocialHandles,
  type SyncFreshness,
} from "../../app/hooks/useConnections.js";
import { openOAuthPopup, popupReturnPath, fallbackReturnPath, onOAuthComplete } from "../../app/oauthPopup.js";
import { pushToast } from "../../app/toasts.js";
import styles from "./SettingsScreen.module.scss";

/** The connect surface (shot-39). A provider's connect MODE decides its affordance:
 *  - `oauth` → a live Connect button (system-browser OAuth): TikTok + Meta ship the connect providers at;
 *               Shopify + Square land the merch connect.
 *  - `soon`   → the connect endpoint lands in a later phase; the card renders its DESIGNED state — "Coming
 *               soon", never a fake zero.
 *  - `identifier` → no OAuth at all: the band PASTES an identifier and we poll with a platform key. YouTube is
 *               the one today. The field takes a channel URL, a bare `UC…` id or an @handle; the server parses
 *               all three, so the card does not validate shapes it would only get wrong.
 *
 * Bandsintown is NOT a card: the integration was retired (2026-10-01 — its terms forbid commercial use), and
 * "Export for Bandsintown" on the Booking page is a CSV download that needs no connection. */
type ConnectMode = "oauth" | "soon" | "identifier";
interface ProviderMeta {
  provider: string;
  label: string;
  category: SectionKey;
  mode: ConnectMode;
  blurb: string;
  /** Shopify and Fourthwall are per-SHOP OAuth: the card first captures the store host, then starts. */
  needsInput?: "shopDomain" | "youtubeChannel";
  /** The placeholder + accessible name for a `shopDomain` card. Per-provider because the two shop-scoped
   *  vendors take different hosts, and a placeholder naming the wrong one is a field nobody can fill. */
  inputHint?: { label: string; placeholder: string };
}

// Grouped by the Connect surface's categories (grouped services, no numeric "N hookups" claim).
//
// NOT here, deliberately (2026-08-19, checked against artist-cntrl-old):
//  • Threads is not a connection — in the old app it is the third PUBLIC HANDLE beside instagram.handle and
//    tiktok.username. It returns as a field on the Public handles card, not as a card of its own.
//  • Laylo is backend-only in BOTH apps (their worker config carries a key pair and a GraphQL URL; no UI, no
//    connect path). It was a placeholder card here, so removing it costs nothing.
const CATALOGUE: ProviderMeta[] = [
  { provider: "spotify", label: "Spotify", category: "analytics", mode: "oauth", blurb: "Sign in to reserve your artist, then we pull your streaming analytics." },
  { provider: "youtube", label: "YouTube", category: "analytics", mode: "identifier", blurb: "Subscribers and video views by channel URL.", needsInput: "youtubeChannel" },
  { provider: "tiktok", label: "TikTok", category: "analytics", mode: "oauth", blurb: "Follower count and your recent videos." },
  { provider: "meta", label: "Instagram & Facebook", category: "analytics", mode: "oauth", blurb: "IG + FB insights, posts, and ad audiences." },
  { provider: "shopify", label: "Shopify", category: "merch", mode: "oauth", blurb: "Online merch sales and inventory.", needsInput: "shopDomain" },
  { provider: "square", label: "Square", category: "merch", mode: "oauth", blurb: "Point-of-sale merch at your shows." },
  // Fourthwall is ONLINE-ONLY by capability (no POS product), which the merch channel picker enforces off the
  // server's own `merchChannels` — this card states nothing about channels itself. Shop-scoped like Shopify.
  {
    provider: "fourthwall", label: "Fourthwall", category: "merch", mode: "oauth",
    blurb: "Online store sales via Fourthwall. You'll approve access in your Fourthwall dashboard — no keys to copy.",
    needsInput: "shopDomain",
    inputHint: { label: "Fourthwall store", placeholder: "your-store.fourthwall.com" },
  },
];

/**
 * The spec's section order: Your data → Analytics platforms → Public social handles → Merch store. The spec's
 * "Live shows" section held only Bandsintown, which is no longer an integration at all, and Calendar sync moved
 * to Settings › Account (PO) beside Google Calendar, as Show Sync.
 *
 * Sections are DECLARED rather than derived from the catalogue, because some hold something that is not a
 * provider card at all — the handles form, the merch lane picker. Grouping by our own categories meant those had
 * to be wedged into the nearest available home (handles inside Social), which is the parity gap this closes.
 */
type SectionKey = "analytics" | "handles" | "merch";

const SECTIONS: { key: SectionKey; title: string; intro?: string }[] = [
  { key: "analytics", title: "Analytics platforms" },
  {
    key: "handles",
    title: "Public social handles",
    intro: "Used by your public release pages, EPK and Link in Bio.",
  },
  { key: "merch", title: "Merch store" },
];

/** Human "last synced" — a compact relative stamp for the card status line.*/
function lastSynced(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * "Your data / Refresh all data" — the spec's banner, as a single line.
 *
 * The spec draws a full-width banner with a heading and a sentence of explanation wrapped around one button.
 * That is a lot of vertical space for something a band presses a handful of times a month, so this carries the
 * same action plus the thing the banner never told them: HOW STALE the data actually is. A timestamp makes the
 * button self-explanatory, which is what the explanatory sentence was there to do.
 *
 * The number is the OLDEST sync across connected sources, not the newest. "Updated 2h ago" beside a source that
 * last synced three days ago would be a lie of omission — the stalest is the only single figure true of
 * everything. Any source that has never synced makes the whole answer unknown rather than reassuring.
 */
function RefreshAllStrip({ freshness: server }: { freshness?: SyncFreshness }) {
  const refresh = useRefreshAllData();

  // SOURCES, not connections. A connection is not necessarily something we pull from — Google Calendar is a
  // push target with no schedule at all — so deriving this from the connected cards reported "Not synced yet"
  // forever for a band whose only connection was Google. The server counts sync_schedules, where push-only
  // providers simply do not appear.
  const sources = server?.sources ?? 0;

  // Always rendered, never conditionally removed. An element that appears and disappears with connection state
  // is more disorienting than a quiet empty state, and born-blank surfaces here render a DESIGNED empty state
  // rather than nothing. What changes is the button: it goes live only when there is something to refresh.
  // "Nothing to refresh yet", not "no data sources": the source/target split is OUR model, not something the
  // band should have to hold. They connected Google Calendar — telling them they have no data sources reads as
  // the app failing to notice. This describes whether the BUTTON can do anything, which is all the strip claims.
  const freshness =
    sources === 0 ? "Nothing to refresh yet"
    : server?.lastSyncOn ? `Updated ${lastSynced(server.lastSyncOn)}`
    : "Not synced yet";

  // Gate d, mirrored. The server computes WHEN a refresh could next pull anything, from each provider's own
  // min_interval_minutes policy row — the client must not re-derive it from a hardcoded hour, because that
  // value is admin-editable and the UI would start lying the moment it was tuned.
  //
  // Without this the button was pressable at any time and simply refused server-side, which reads as a dead
  // button: no spinner, no change, no explanation.
  // Minutes until a refresh could pull again; 0 when it can pull now. Kept as a number so the narrowing is
  // plain and the copy does not have to reverse-engineer a relative-time string.
  const waitMinutes = server?.nextSyncAllowedOn
    ? Math.max(0, Math.ceil((new Date(server.nextSyncAllowedOn).getTime() - Date.now()) / 60_000))
    : 0;
  const rateLimited = waitMinutes > 0;

  return (
    <div className={styles.dataStrip}>
      <span className={styles.dataFreshness}>{freshness}</span>
      <Button
        variant="accent"
        size="xs"
        loading={refresh.isPending || server?.syncing === true}
        // Disabled rather than hidden: the affordance stays discoverable, and pressing it with no schedules
        // would spend a rate-limit token to accomplish nothing. Also disabled while a sync is already in
        // flight — a second press cannot make it arrive sooner and only burns quota.
        disabled={sources === 0 || rateLimited || server?.syncing === true}
        title={
          rateLimited
            ? `Already refreshed recently — available again in ${waitMinutes} min.`
            : server?.syncing
              ? "A refresh is already running."
              : undefined
        }
        onClick={() => refresh.mutate()}
      >
        Refresh all data
      </Button>
    </div>
  );
}

const HANDLE_FIELDS = [
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "threads", label: "Threads" },
] as const;

/**
 * "Public social handles" — the band's @-handles for the public release pages, EPK and Link in Bio.
 *
 * These are NOT connections and deliberately do not look like one: nothing is granted, nothing expires, and
 * there is nothing to disconnect. Threads has no OAuth at all and TikTok's omits the handle scope, so a typed
 * handle is the ONLY way those links can exist — and for Instagram a typed handle overrides whatever the Meta
 * connection reported, which is how a band corrects a wrong one.
 *
 * The form is the whole truth on save: an emptied field CLEARS that handle server-side, so "leave it blank to
 * remove it" works the way a person expects.
 */
function SocialHandlesCard({ saved }: { saved: SocialHandles }) {
  const update = useUpdateSocialHandles();
  const [draft, setDraft] = useState<Record<string, string>>({});

  // The server's stored value is the baseline; `draft` only holds fields the user has actually touched, so a
  // background refetch (the connection-changed signal) never clobbers half-typed input.
  const valueOf = (key: string) =>
    draft[key] ?? (saved[key as keyof SocialHandles] as string | null | undefined) ?? "";
  const dirty = HANDLE_FIELDS.some(
    ({ key }) => key in draft && draft[key] !== ((saved[key as keyof SocialHandles] as string | null) ?? ""),
  );

  const setCount = saved.setCount ?? 0;
  const status = setCount === 0 ? undefined : setCount === HANDLE_FIELDS.length ? "connected" : "partial";
  const statusLabel =
    setCount === 0 ? "Not set"
    : setCount === HANDLE_FIELDS.length ? "Set"
    : `${setCount} / ${HANDLE_FIELDS.length} set`;

  // Rendered as a flex child of the group rather than a cell of the 2-up grid, so it already spans the full
  // width the spec gives `.conn-card--full` — three fields would be crushed into half a row.
  return (
    <article className={styles.connCard}>
      <div className={styles.connHead}>
        <span className={styles.connName}>Public handles</span>
        <span className={styles.badge} data-status={status}>
          {statusLabel}
        </span>
      </div>
      <p className={styles.connBlurb}>
        Public handles for your release pages, Link in Bio, and EPK. Type the @handle — no URL needed.
      </p>

      <div className={styles.handleFields}>
        {HANDLE_FIELDS.map(({ key, label }) => (
          <Field
            key={key}
            label={label}
            placeholder="@yourband"
            value={valueOf(key)}
            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
          />
        ))}
      </div>

      <div className={styles.connActions}>
        <Button
          variant="accent"
          size="sm"
          loading={update.isPending}
          disabled={!dirty}
          onClick={() => {
            update
              .mutateAsync({
                instagram: valueOf("instagram"),
                tiktok: valueOf("tiktok"),
                threads: valueOf("threads"),
              })
              .then(() => setDraft({}))   // the server's normalized answer becomes the new baseline
              .catch(() => {
                /* the hook toasts the typed 422; the draft stays so nothing typed is lost */
              });
          }}
        >
          Save handles
        </Button>
      </div>
    </article>
  );
}

const LANES: { channel: MerchChannel; label: string; subLabel: string }[] = [
  { channel: "online", label: "Online sales", subLabel: "Feeds the merch figure on your dashboard." },
  { channel: "pos", label: "In-person sales", subLabel: "Feeds the merch total on each show's settlement." },
];

/**
 * "Assign each store a sales channel" — which connected store feeds each merch lane.
 *
 * Only rendered when the band has a connected merch store, and each lane only offers the stores the SERVER
 * says can serve it (`merchChannels`), so the picker can't present a choice the write would reject with a 422.
 *
 * "Automatic" is the real absence of a nomination, not a sentinel: the server falls back to the
 * earliest-connected capable store, which is what every band saw before this control existed. That is why it
 * is the default option rather than a hidden state — a band that never opens this still has a truthful answer
 * shown to them.
 */
/** Compact follower count — the number that tells four identically-named artists apart at a glance. */
function formatFollowers(n: number | null | undefined): string | null {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n < 10_000_000 ? 1 : 0).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, "")}K`;
  return `${n}`;
}

/**
 * The artist half of the Spotify card — rendered INSIDE it, not beside it.
 *
 * There is one Spotify integration, so it gets one card. Signing in and naming the artist are two steps of
 * the same connection; splitting them across two cards implied a second thing to connect.
 *
 * **Why signing in is not the whole job.** The artist id is the key every DSP number we show is resolved by,
 * and it is unique across the whole platform: one band holds it, and the next to ask is refused. Signing in
 * proves a Spotify account exists behind the claim; it does not say WHICH artist that account is, because
 * Spotify's API has no "artists I manage". So the band picks, and the sign-in is what makes the pick
 * attributable rather than anonymous.
 *
 * **Why a picker rather than a field for the id.** Picking the wrong artist takes an id that belongs to
 * someone else and points this band's analytics at a stranger — an error nothing on screen would reveal. The
 * name, image and follower count make it obvious. A pasted artist link still works: the server resolves it to
 * that one artist, so a band who already knows their id is not made to search for it.
 */
function SpotifyArtistPanel({ reservation }: { reservation?: SpotifyArtistReservation | null }) {
  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  // -1 = nothing highlighted. It must NOT start at 0, or "Enter takes the highlighted row" becomes
  // auto-picking the first match — the exact silent-wrong-artist failure the picker exists to prevent.
  const [highlight, setHighlight] = useState(-1);
  const [changing, setChanging] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const search = useSpotifyArtistSearch(query);
  const claim = useClaimSpotifyArtist();
  useDismiss(listOpen, () => setListOpen(false), boxRef);

  const results = search.data?.items ?? [];
  const searching = query.trim().length >= 2;
  const unavailable = search.data?.providerConfigured === false;


  function pick(hit: SpotifyArtistHit) {
    if (!hit.artistId) return;
    setListOpen(false);
    setQuery("");
    setChanging(false);
    claim.mutate({ artist: hit.artistId });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // A pasted link or id is not a search — Enter reserves it directly, so the band who already knows their
    // id never has to wait for a lookup they did not need.
    if (e.key === "Enter" && results.length === 0 && query.trim().length > 0) {
      e.preventDefault();
      claim.mutate({ artist: query.trim() });
      setQuery("");
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setListOpen(true);
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // One match ⇒ Enter takes it. Several ⇒ only a row deliberately arrowed onto; with nothing highlighted
      // there is no defensible guess among four artists with the same name.
      const chosen = results.length === 1 ? results[0] : highlight >= 0 ? results[highlight] : undefined;
      if (chosen) pick(chosen);
    }
  }

  // The claimed artist, with its own change affordance on the same line — so the card keeps ONE row of
  // buttons (the connection's own Disconnect) instead of growing a second.
  if (reservation && !changing) {
    return (
      <>
        <div className={styles.artistClaimed}>
          {reservation.imageUrl ? (
            <img src={reservation.imageUrl} alt="" className={styles.artistClaimedAvatar} loading="lazy" />
          ) : (
            <span className={styles.artistClaimedAvatar} aria-hidden="true" />
          )}
          <span className={styles.artistClaimedName}>
              {reservation.name ?? reservation.artistId}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setChanging(true)}>
            Change
          </Button>
        </div>
        <span className={styles.connMeta}>Streaming and audience numbers are pulled for this artist.</span>
      </>
    );
  }

  return (
    <>
      <p className={styles.connBlurb}>
        {changing
          ? "Pick a different artist. The one you hold now is released when the new claim lands."
          : "Now choose your artist. Only one CNTRL account can hold an artist."}
      </p>

          <div className={styles.artistSearchBox} ref={boxRef}>
            <input
              type="text"
              role="combobox"
              aria-expanded={listOpen && results.length > 0}
              aria-controls="spotify-artist-results"
              aria-autocomplete="list"
              aria-label="Search for your artist on Spotify"
              className={styles.connInput}
              placeholder="Your artist name, or open.spotify.com/artist/…"
              value={query}
              disabled={claim.isPending}
              onChange={(e) => {
                setQuery(e.target.value);
                setListOpen(true);
                setHighlight(-1);   // a new query carries no deliberate selection
              }}
              onFocus={() => setListOpen(true)}
              onKeyDown={onKeyDown}
            />

            {listOpen && searching && (
              <ul id="spotify-artist-results" role="listbox" className={styles.artistResults}>
                {search.isFetching && <li className={styles.artistResultsNote}>Searching…</li>}
                {!search.isFetching && unavailable && (
                  <li className={styles.artistResultsNote}>
                    Artist search is unavailable right now — paste your artist link instead.
                  </li>
                )}
                {!search.isFetching && !unavailable && results.length === 0 && (
                  <li className={styles.artistResultsNote}>No artists found.</li>
                )}
                {!search.isFetching &&
                  results.map((hit, i) => {
                    const followers = formatFollowers(hit.followers);
                    return (
                      <li key={hit.artistId}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={i === highlight}
                          className={styles.artistResult}
                          data-highlight={i === highlight || undefined}
                          onMouseEnter={() => setHighlight(i)}
                          onClick={() => pick(hit)}
                        >
                          {hit.imageUrl ? (
                            <img src={hit.imageUrl} alt="" className={styles.artistAvatar} loading="lazy" />
                          ) : (
                            <span className={styles.artistAvatar} aria-hidden="true" />
                          )}
                          <span className={styles.artistName}>{hit.name}</span>
                          {followers && (
                            <span className={styles.artistFollowers}>{followers} followers</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
      {changing && (
        <div className={styles.connActions}>
          <Button variant="ghost" size="sm" onClick={() => setChanging(false)}>
            Cancel
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * The CREATOR-DATA link — the last rung of the Chartmetric artist-resolution ladder, and the only rung a
 * person ever sees.
 *
 * **Why this card exists at all.** Top Creators, and the UGC tracker beside it, come from Chartmetric, which
 * has no connection of its own — nothing to sign into, no token, nothing to disconnect. The band is normally
 * identified by an id we already hold: Chartmetric's `get-ids` maps a Spotify artist id or a YouTube channel
 * id onto its own artist, exactly, with no name matching anywhere in it. That covers almost everyone and it
 * happens silently, which is why this card says nothing when it worked.
 *
 * **Why it is not always shown.** A band whose link resolved has no decision to make and has never heard of
 * Chartmetric; putting a vendor's name in front of them would be asking them to care about our plumbing. So
 * the card appears in exactly two states: the link is CLAIMED (theirs to change), or nothing can resolve it
 * (theirs to set). A derived link renders nothing.
 *
 * **Why a picker and never a search-and-take.** The legacy app resolved this by searching the band's name and
 * storing the top hit, which fed a band called "Mae" a much larger stranger's numbers for a day. No better
 * query fixes that — two artists really do share a name — so the choice is a person's, and the search here
 * has no authority to write anything. The rows carry picture, verified and follower count because those are
 * what actually tell two same-named artists apart; a list of identical names would just move the coin flip.
 */
function ChartmetricArtistCard({ link }: { link?: ChartmetricArtistLinkView | null }) {
  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  // -1, for the reason the Spotify picker states: starting at 0 turns "Enter takes the highlighted row" into
  // auto-picking the first match, which IS the failure this card exists to prevent.
  const [highlight, setHighlight] = useState(-1);
  const [changing, setChanging] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const search = useChartmetricArtistSearch(query);
  const claim = useClaimChartmetricArtist();
  useDismiss(listOpen, () => setListOpen(false), boxRef);

  const results = search.data?.items ?? [];
  const searching = query.trim().length >= 2;
  const unavailable = search.data?.providerConfigured === false;

  function pick(hit: ChartmetricArtistHit) {
    if (!hit.id) return;
    setListOpen(false);
    setQuery("");
    setChanging(false);
    claim.mutate({ artist: hit.id });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setListOpen(true);
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // One match ⇒ Enter takes it. Several ⇒ only a row deliberately arrowed onto. There is deliberately no
      // "Enter submits the raw text" branch here, unlike the Spotify picker: that one accepts a pasted artist
      // LINK, and a Chartmetric artist page is not something a band has any reason to be holding — so free
      // text could only ever be a name, which is the guess this card replaces.
      const chosen = results.length === 1 ? results[0] : highlight >= 0 ? results[highlight] : undefined;
      if (chosen) pick(chosen);
    }
  }

  if (link?.claimed && !changing) {
    return (
      <article className={styles.connCard}>
        <div className={styles.connHead}>
          <span className={styles.connName}>Creator data</span>
          <span className={styles.badge} data-status="connected">Linked</span>
        </div>
        <div className={styles.artistClaimed}>
          <span className={styles.artistClaimedAvatar} aria-hidden="true" />
          <span className={styles.artistClaimedName}>{link.name ?? link.externalId}</span>
          <Button variant="ghost" size="sm" onClick={() => setChanging(true)}>
            Change
          </Button>
        </div>
        <span className={styles.connMeta}>Top followers and UGC are pulled for this artist.</span>
      </article>
    );
  }

  return (
    <article className={styles.connCard}>
      <div className={styles.connHead}>
        <span className={styles.connName}>Creator data</span>
        <span className={styles.badge} data-status={changing ? "connected" : undefined}>
          {changing ? "Linked" : "Not set"}
        </span>
      </div>
      <p className={styles.connBlurb}>
        {changing
          ? "Pick a different artist. Top followers and UGC will be pulled for the one you choose."
          : "We could not work out which artist you are from your other connections. Pick yourself here and we can show your top creators."}
      </p>

      <div className={styles.artistSearchBox} ref={boxRef}>
        <input
          type="text"
          role="combobox"
          aria-expanded={listOpen && results.length > 0}
          aria-controls="chartmetric-artist-results"
          aria-autocomplete="list"
          // Deliberately shares no leading phrase with the Spotify picker's "Search for your artist on
          // Spotify". Accessible names are matched by SUBSTRING as often as not, so a name merely EXTENDING
          // another's is ambiguous to a screen-reader user picking from a list and to every `name:` query.
          aria-label="Find your artist for creator data"
          className={styles.connInput}
          placeholder="Your artist name"
          value={query}
          disabled={claim.isPending}
          onChange={(e) => {
            setQuery(e.target.value);
            setListOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setListOpen(true)}
          onKeyDown={onKeyDown}
        />

        {listOpen && searching && (
          <ul id="chartmetric-artist-results" role="listbox" className={styles.artistResults}>
            {search.isFetching && <li className={styles.artistResultsNote}>Searching…</li>}
            {!search.isFetching && unavailable && (
              <li className={styles.artistResultsNote}>Artist search is unavailable right now.</li>
            )}
            {!search.isFetching && !unavailable && results.length === 0 && (
              <li className={styles.artistResultsNote}>No artists found.</li>
            )}
            {!search.isFetching &&
              results.map((hit, i) => {
                const followers = formatFollowers(hit.followers);
                return (
                  <li key={hit.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={i === highlight}
                      className={styles.artistResult}
                      data-highlight={i === highlight || undefined}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => pick(hit)}
                    >
                      {hit.imageUrl ? (
                        <img src={hit.imageUrl} alt="" className={styles.artistAvatar} loading="lazy" />
                      ) : (
                        <span className={styles.artistAvatar} aria-hidden="true" />
                      )}
                      <span className={styles.artistName}>{hit.name}</span>
                      {/* Verified and follower count share the meta slot rather than the verified mark
                          getting an icon of its own: both are here to TELL TWO ARTISTS APART, and a badge
                          that only some rows carry reads as a quality score rather than a disambiguator.
                          Visible text, not a screen-reader-only aside — the sighted user picking between two
                          identical names needs it at least as much. */}
                      {(hit.verified || followers) && (
                        <span className={styles.artistFollowers}>
                          {[hit.verified ? "Verified" : null, followers ? `${followers} followers` : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
          </ul>
        )}
      </div>

      {changing && (
        <div className={styles.connActions}>
          <Button variant="ghost" size="sm" onClick={() => setChanging(false)}>
            Cancel
          </Button>
        </div>
      )}
    </article>
  );
}

function MerchChannelPicker({ stores }: { stores: ConnectionStatus[] }) {
  const assign = useAssignMerchChannel();
  // The vendor's display name, not the raw provider key. Only one LIVE connection exists per (band, vendor),
  // so the vendor name identifies a store unambiguously and the shop domain would only add noise.
  //
  // The fallback humanises rather than printing the key, because this list is driven by the SERVER's capability
  // answer: a vendor the backend admits before this catalogue does would otherwise render as its raw provider
  // id. Fourthwall was that vendor until its card landed; the fallback stays for the next one.
  const nameOf = (provider: string | null | undefined) =>
    CATALOGUE.find((p) => p.provider === provider)?.label ??
    (provider ?? "")
      .split(/[_-]/)
      .filter(Boolean)
      .map((w) => w[0]!.toUpperCase() + w.slice(1))
      .join(" ");

  return (
    <div className={styles.connLanes}>
      {LANES.map(({ channel, label, subLabel }) => {
        const eligible = stores.filter((s) => s.merchChannels?.includes(channel));
        if (eligible.length === 0) return null;

        const assigned = eligible.find((s) => (channel === "online" ? s.assignedOnline : s.assignedPos));
        const pending = assign.isPending && assign.variables?.channel === channel;

        return (
          <Picker
            key={channel}
            label={label}
            labelVariant="heading"
            subLabel={subLabel}
            value={assigned?.provider ?? ""}
            disabled={pending}
            options={[
              { value: "", label: "Automatic (earliest connected)" },
              ...eligible.map((s) => ({ value: s.provider ?? "", label: nameOf(s.provider) })),
            ]}
            onChange={(v) => {
              const provider = v === "" ? null : v;
              assign.mutate({
                channel,
                provider,
                message: provider
                  ? `${label} now come from ${nameOf(provider)}.`
                  : `${label} will use your earliest connected store.`,
              });
            }}
          />
        );
      })}
    </div>
  );
}


/** The store the Shopify App URL sent here (`?shop=`), if it is one. The server already normalised it; this only
 * refuses anything that is not a store host, since it ends up in a field. */
function shopifyInstallShop(): string | null {
  const shop = new URLSearchParams(window.location.search).get("shop")?.trim().toLowerCase() ?? "";
  return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop) ? shop : null;
}

export function SettingsConnectionsTab() {
  // NON-PRODUCTION only; the API refuses on the same allowlist, so this is convenience, not the gate.
  const devConnect = useConnectDevArtist();
  const connections = useConnections();
  const canManage = useCapability("connections:manage");
  // The handles write is band:manage — public identity, a different bar from writing a connection.
  const canManageBand = useCapability("band:manage");
  const start = useStartConnection();
  const disconnect = useDisconnectConnection();
  // The per-shop store host captured before Connect starts its OAuth, KEYED BY PROVIDER. Shopify and
  // Fourthwall are both shop-scoped and both render in the merch section at the same time, so a single shared
  // string would echo whatever was typed in one card into the other's field.
  // Arriving from the Shopify app's App URL (ART-339) carries `?shop={store}.myshopify.com`: the store is
  // prefilled so the band only has to press Connect. Connect itself is still the press (a popup opened without a
  // click is blocked), so nothing starts on its own.
  const [shopDomains, setShopDomains] = useState<Record<string, string>>(() => {
    const shop = shopifyInstallShop();
    const initial: Record<string, string> = {};
    if (shop) initial.shopify = shop;
    return initial;
  });
  const installShopAnnounced = useRef(false);
  useEffect(() => {
    const shop = shopifyInstallShop();
    if (!shop || installShopAnnounced.current) return;
    installShopAnnounced.current = true;
    pushToast(`Press Connect on Shopify to link ${shop}.`, "info");
  }, []);
  const shopDomainOf = (provider: string) => shopDomains[provider] ?? "";
  // The pasted YouTube channel — a URL, a bare UC… id or an @handle. Deliberately NOT validated here: the
  // server parses every shape a band might copy out of a browser, and a field that rejected one of them would
  // be wrong in a way the band cannot debug.
  const [ytChannel, setYtChannel] = useState("");
  const connectYouTube = useConnectYouTube();

  // The popup reports back here. The server also publishes `connection-changed` over the realtime plane,
  // but that is at-most-once and may not be connected — this makes the repaint deterministic for the seat
  // that actually did the connecting, which is the one watching.
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  useEffect(
    () =>
      onOAuthComplete(({ provider, ok }) => {
        const label = CATALOGUE.find((p) => p.provider === provider)?.label ?? provider;
        pushToast(ok ? `Connected to ${label}.` : `Couldn't connect to ${label}.`, ok ? "info" : "error");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      }),
    [qc, bandId],
  );

  if (connections.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading connections…" />
      </div>
    );
  }
  if (connections.isError) {
    return (
      <EmptyState
        title="Couldn't load connections"
        description={connections.error instanceof Error ? connections.error.message : "Please try again."}
        action={<Button onClick={() => void connections.refetch()}>Retry</Button>}
      />
    );
  }

  // The live status per provider (born-blank: absent ⇒ not connected). Revoked rows never come back from the API.
  const statusByProvider = new Map<string, ConnectionStatus>();
  for (const c of connections.data?.items ?? []) {
    if (c.provider) statusByProvider.set(c.provider, c);
  }

  // Which providers this HOST can connect AT ALL — the just-in-time-credential state, served per provider
  // because it is a fact about the environment rather than about this band.
  //
  // A provider ABSENT from the map is not "unavailable": only authorization-code OAuth connects are registered
  // server-side, so YouTube (an identifier paste) is legitimately missing and must render exactly as before.
  // Only an explicit `false` suppresses a Connect — read it that way at the call site, never as a truthiness
  // test, or the non-OAuth card goes dark for everyone.
  const configuredByProvider = new Map<string, boolean>();
  for (const p of connections.data?.providers ?? []) {
    if (p.provider) configuredByProvider.set(p.provider, p.configured === true);
  }

  // The band's live merch stores — identified by the SERVER's capability answer, so this list needs no local
  // copy of which vendors sell merch and picks up a new one the moment the backend admits it.
  const merchStores = (connections.data?.items ?? []).filter(
    (c) => c.connected && (c.merchChannels?.length ?? 0) > 0,
  );

  const onConnect = (meta: ProviderMeta) => {
    // Open the popup SYNCHRONOUSLY, before any await. A window.open that happens after the authorize URL
    // comes back has lost its user-gesture provenance and every major browser blocks it.
    const popup = openOAuthPopup();

    void start
      .mutateAsync({
        provider: meta.provider,
        // In popup mode the return path is the marker route that tells the returning document to close
        // itself — the band never sees it. In fallback mode we come back to the page they were ON, with a
        // marker that reopens Settings on this tab, rather than dumping them at a fixed route.
        returnUrl: popup ? popupReturnPath() : fallbackReturnPath(),
        // Only the shop-scoped providers thread a store host; every other provider starts bare-OAuth.
        shopDomain: meta.needsInput === "shopDomain" ? shopDomainOf(meta.provider).trim() : undefined,
      })
      .then((res) => {
        if (!res.authorizeUrl) {
          popup?.close();
          return;
        }
        if (popup) popup.location.href = res.authorizeUrl;
        else window.location.assign(res.authorizeUrl);   // popup blocked — the old full-page hop
      })
      .catch(() => {
        popup?.close();   // never strand an empty about:blank window on a failed start
        /* the hook already surfaced the typed 404/502 message via toast */
      });
  };

  return (
    <div className={styles.stack}>
      {/* The spec opens the tab with the "Your data" banner; this is the same action at a quarter the height. */}
      <RefreshAllStrip freshness={connections.data?.freshness} />

      {SECTIONS.map(({ key, title, intro }) => {
        const cards = CATALOGUE.filter((p) => p.category === key);

        // A section whose only content is gated must disappear WITH it. Handles holds no
        // provider cards at all, so for a seat without band:manage they would otherwise render as a bare
        // heading over nothing — which reads as a broken section rather than one that does not apply.
        const hasContent =
          cards.length > 0 || (key === "handles" && canManageBand);
        if (!hasContent) return null;

        return (
        <section key={key} className={styles.connGroup} aria-labelledby={`conn-${key}`}>
          <h3 id={`conn-${key}`} className={styles.connGroupTitle}>
            {title}
          </h3>
          {intro && <p className={styles.connIntro}>{intro}</p>}
          {cards.length > 0 && (
          <div className={styles.connGrid}>
            {cards.map((meta) => {
              const status = statusByProvider.get(meta.provider);
              const connected = status?.connected === true;
              const errored = status?.status === "error";
              const synced = lastSynced(status?.lastSyncOn);
              const connecting = start.isPending && start.variables?.provider === meta.provider;
              const disconnecting =
                disconnect.isPending && disconnect.variables?.provider === meta.provider;

              // This host holds no credentials for the provider, so its connect can only ever answer 502
              // `provider-unavailable`. Rendered as its own state rather than as a Not-connected card with a
              // live button: the two are different sentences — "nobody can connect this here" is ours to fix
              // and "you have not connected it yet" is theirs — and collapsing them is what let a correct
              // merch card be reported broken on a lane that was simply never given keys (ART-33). The button
              // it replaces was dead in the strictest sense: pressing it opened a popup and shut it again.
              //
              // Scoped to the UNCONNECTED card only. A band that connected before the keys were removed keeps
              // its Connected/Reconnect row and its Disconnect, which needs no vendor credential to work.
              const setupPending =
                configuredByProvider.get(meta.provider) === false && !connected && !errored;

              const pill = connected ? "connected" : errored ? "error" : setupPending ? "soon" : undefined;
              const pillLabel = connected
                ? "Connected"
                : errored
                  ? "Reconnect"
                  : setupPending
                    ? "Setup pending"
                    : "Not connected";
              // A shop-scoped provider's store host is required before Connect; the prompt only shows on the
              // fresh (unconnected) card.
              // `setupPending` suppresses every typed-input row too: a field you can fill and then cannot
              // submit is worse than no field, and the store host is only ever read by a connect that cannot run.
              const wantsShopDomain =
                meta.needsInput === "shopDomain" && !connected && !errored && !setupPending;
              const shopDomainValue = shopDomainOf(meta.provider);
              const shopDomainMissing = wantsShopDomain && shopDomainValue.trim() === "";
              const wantsYouTubeChannel =
                meta.needsInput === "youtubeChannel" && !connected && !setupPending;
              const ytMissing = wantsYouTubeChannel && ytChannel.trim() === "";
              // Providers that need something typed before they can connect. Both are false once connected,
              // so a connected card always falls through to the ordinary Disconnect row.
              const hasInlineInput = wantsShopDomain || wantsYouTubeChannel;

              return (
                <article key={meta.provider} className={styles.connCard}>
                  <div className={styles.connHead}>
                    {/* The mark identifies the vendor at a glance — nominative use, and the label carries the
                        accessible name, so the glyph is decorative. A provider with no vendored mark (only
                        Fourthwall today) falls back to a Lucide glyph rather than to nothing. */}
                    <PlatformIcon platform={meta.provider} size={20} />
                    <span className={styles.connName}>{meta.label}</span>
                    <span className={styles.badge} data-status={pill}>
                      {pillLabel}
                    </span>
                  </div>

                  <p className={styles.connBlurb}>
                    {/* Spotify's handle is the ACCOUNT that signed in, not the artist that account reserved —
                        and the reserved artist renders just below it. Left bare, one name would sit above
                        another with nothing saying which is which, so this one says. */}
                    {connected && status?.displayHandle
                      ? meta.provider === "spotify"
                        ? `Signed in as ${status.displayHandle}`
                        : status.displayHandle
                      : meta.blurb}
                  </p>
                  {connected && synced && <span className={styles.connMeta}>Synced {synced}</span>}
                  {errored && status?.lastError && <p className={styles.connError}>{status.lastError}</p>}

                  {canManage && (
                    <>
                      {/* Spotify's second step, inside its own card: the sign-in above is what gates this
                          claim, and there is only one Spotify integration to connect. */}
                      {meta.provider === "spotify" && connected && (
                        <SpotifyArtistPanel reservation={connections.data?.spotifyArtist} />
                      )}

                      {/* A provider that needs something TYPED puts its Connect beside the field rather than
                          under it. Stacked, the two providers with a field made their whole grid row taller
                          than the row below, so the cards without one carried dead space. Side by side, an
                          input row costs the same height as the button row every other card ends with.
                          No visible label: it would put the height straight back, and the placeholder already
                          names the thing exactly. The accessible name rides aria-label (the artist picker's
                          shape — this tab now types its inputs one way). */}
                      {hasInlineInput ? (
                        <div className={styles.connInputRow}>
                          <input
                            type="text"
                            className={styles.connInput}
                            aria-label={
                              wantsShopDomain ? (meta.inputHint?.label ?? "Shop domain") : "Channel URL"
                            }
                            placeholder={
                              wantsShopDomain
                                ? (meta.inputHint?.placeholder ?? "your-store.myshopify.com")
                                : "youtube.com/@yourband"
                            }
                            value={wantsShopDomain ? shopDomainValue : ytChannel}
                            onChange={(e) =>
                              wantsShopDomain
                                ? setShopDomains((prev) => ({ ...prev, [meta.provider]: e.target.value }))
                                : setYtChannel(e.target.value)
                            }
                          />
                          {wantsShopDomain ? (
                            <Button
                              variant="accent"
                              size="sm"
                              loading={connecting}
                              disabled={shopDomainMissing}
                              onClick={() => onConnect(meta)}
                            >
                              Connect
                            </Button>
                          ) : (
                            <Button
                              variant="accent"
                              size="sm"
                              loading={connectYouTube.isPending}
                              disabled={ytMissing}
                              onClick={() => connectYouTube.mutate({ channel: ytChannel.trim() })}
                            >
                              Connect
                            </Button>
                          )}
                        </div>
                      ) : (
                      <div className={styles.connActions}>
                        {connected || errored ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={disconnecting}
                            onClick={() => void disconnect.mutate({ provider: meta.provider, label: meta.label })}
                          >
                            {errored ? "Remove" : "Disconnect"}
                          </Button>
                        ) : setupPending ? (
                          /* Ordered BEFORE the mode branches on purpose: an unkeyed provider's mode is
                             irrelevant, because none of its connect paths can run. Disabled-with-a-reason
                             rather than absent — the card is how a band learns the integration exists and is
                             coming, which a missing card cannot say. */
                          <>
                            {/* Still a Connect, and still disabled, so the card keeps the same shape as every
                                other one in the grid — a card that simply loses its action row reads as
                                half-rendered. The badge above says WHICH state this is and the line below says
                                why, so the disabled control is explained rather than dead. */}
                            <Button variant="ghost" size="sm" disabled>
                              Connect
                            </Button>
                            <span className={styles.connMeta}>
                              Not switched on for this environment yet.
                            </span>
                          </>
                        ) : meta.mode === "oauth" ? (
                          <>
                            {/* Bare-OAuth only. A provider that needs a typed value never reaches here — its
                                Connect lives in the input row above, and once connected this branch is not
                                taken at all. */}
                            <Button variant="accent" size="sm" loading={connecting} onClick={() => onConnect(meta)}>
                              Connect
                            </Button>
                            {/* NON-PRODUCTION escape hatch, beside the real Connect rather than inside the
                                artist panel — that panel only renders once `connected` is true, which is the
                                very gate this bypasses, so a button placed there could never be reached on a
                                fresh dev band. Spotify's OAuth cannot succeed outside production (the Feb 2026
                                development-mode allowlist), so without this a dev band can never attach to the
                                analytics spine through the product at all. It generates the id itself — there
                                is nothing to type and nothing to choose. */}
                            {IS_NON_PRODUCTION && meta.provider === "spotify" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={devConnect.isPending}
                                onClick={() => devConnect.mutate()}
                                title="Dev only: give this band a Spotify connection without the sign-in, which cannot succeed outside production."
                              >
                                Override (dev)
                              </Button>
                            )}
                            {/* A silent failure here reads as a dead button. It did: a 402 plan-inactive was
                                refused server-side and nothing on screen said so. */}
                            {devConnect.error && (
                              <span className={styles.connError}>
                                {errorMessage(devConnect.error, "Override failed.")}
                              </span>
                            )}
                          </>
                        ) : (
                          <Button variant="ghost" size="sm" disabled>
                            Coming soon
                          </Button>
                        )}
                      </div>
                      )}
                    </>
                  )}
                </article>
              );
            })}
          </div>
          )}

          {/* Each section owns its non-card content now, rather than borrowing another section's slot. */}

          {/* Public handles are band:manage — public IDENTITY, not a connection, matching the endpoint. */}
          {key === "handles" && canManageBand && (
            <SocialHandlesCard saved={connections.data?.handles ?? {}} />
          )}

          {/* The creator-data link, under the analytics providers because it is downstream of them: it is
              normally derived from the Spotify artist or the YouTube channel above, and only needs a person
              when neither is there. `chartmetricArtist` null + no chainable id is the one unresolvable state;
              a derived link renders nothing, because a working link is not a decision anyone has to make. */}
          {key === "analytics" && canManage && (() => {
            const link = connections.data?.chartmetricArtist;
            // The only two ids Chartmetric's get-ids will accept are a Spotify ARTIST id and a YouTube
            // channel id. Note this asks whether Spotify is CONNECTED, not whether an artist is claimed yet:
            // an unclaimed Spotify card is already showing its own picker two cards up, and that pick is the
            // better route (it resolves the whole DSP spine, not just creators). Two "which artist are you?"
            // pickers on one tab is a worse tab than one.
            const resolvable =
              statusByProvider.get("spotify")?.connected === true ||
              statusByProvider.get("youtube")?.connected === true;
            // ...and only ask once the band has connected SOMETHING. With nothing connected at all, "we could
            // not work out which artist you are from your other connections" describes a problem that does not
            // exist yet and reads as an error over a born-blank tab — the same mistake as telling a band to
            // reconnect a platform that was never connected.
            const hasAnalytics = ["spotify", "youtube", "tiktok", "meta"].some(
              (p) => statusByProvider.get(p)?.connected === true,
            );
            // Claimed ⇒ theirs to change. Connected, nothing resolved, no route to resolve one ⇒ theirs to
            // set. A DERIVED link renders nothing: it works, and it is not a decision anyone has to make.
            return link?.claimed || (!link && !resolvable && hasAnalytics)
              ? <ChartmetricArtistCard link={link} />
              : null;
          })()}

          {/* Which store feeds which lane. A property OF the merch cards, so it sits under them. */}
          {key === "merch" && canManage && merchStores.length > 0 && (
            <MerchChannelPicker stores={merchStores} />
          )}

          {/* The ICS subscribe card. band:manage — the feed token is a bearer URL to the band's schedule. */}
        </section>
        );
      })}
    </div>
  );
}
